// ─── Design tokens ────────────────────────────────────────────────────────────

export const colors = {
  panel: '#252931',
  tableHeaderBg: '#374151',
  textPrimary: '#f3f4f7',
  textSecondary: '#9aa4b2',
  border: 'rgba(255, 255, 255, 0.08)',
  accent: '#1aeec2',
};

// ─── Layout ───────────────────────────────────────────────────────────────────

export const pageShellStyles = {
  px: { xs: 0, md: 0 },
  pb: 1,
};

export const subTabSx = {
  mb: 2,
  minHeight: 0,
  borderBottom: `1px solid ${colors.border}`,
  '& .MuiTab-root': {
    color: colors.textSecondary,
    fontWeight: 400,
    minHeight: 0,
    px: 3,
    py: 1.5,
    fontSize: '1rem',
    fontFamily: 'Questrial, sans-serif',
    background: 'transparent',
    borderRadius: '8px 8px 0 0',
    textTransform: 'none',
    letterSpacing: '0.5px',
  },
  '& .Mui-selected': {
    color: colors.accent,
    background: 'rgba(26, 238, 194, 0.08)',
    fontWeight: 600,
  },
  '& .MuiTabs-indicator': {
    backgroundColor: colors.accent,
    height: 2,
  },
};

// ─── Alerts (reusable) ────────────────────────────────────────────────────────

export const alertErrorSx = {
  mb: 2,
  backgroundColor: 'rgba(255, 59, 48, 0.1)',
  color: '#ff3b30',
  '& .MuiAlert-icon': { color: '#ff3b30' },
};

export const alertSuccessSx = {
  mb: 2,
  backgroundColor: 'rgba(76, 175, 80, 0.1)',
  color: '#4caf50',
  '& .MuiAlert-icon': { color: '#4caf50' },
};

export const tableAlertErrorSx = {
  mt: 2,
  borderRadius: '12px',
  backgroundColor: 'rgba(244, 67, 54, 0.08)',
  color: colors.textPrimary,
  border: '1px solid rgba(244, 67, 54, 0.18)',
};

// ─── Management tab ───────────────────────────────────────────────────────────

export const managementBoxSx = { maxWidth: 560 };

/** Full contract / wallet address in management summary cards. */
export const contractAddressValueSx = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  color: colors.textPrimary,
  wordBreak: 'break-all',
  lineHeight: 1.45,
};

export const saveButtonSx = {
  mt: 3,
  height: 48,
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '1rem',
  fontFamily: 'Questrial, sans-serif',
  minWidth: 160,
};

// ─── Revenue Items header ─────────────────────────────────────────────────────

export const tabHeaderRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: { xs: 'flex-start', md: 'center' },
  flexDirection: { xs: 'column', md: 'row' },
  gap: 2,
  mb: 2.5,
};

export const filterToolbarSx = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 2,
  mb: 2,
  alignItems: 'flex-end',
};

export const pageTitleSx = {
  color: colors.textPrimary,
  fontSize: { xs: '1.6rem', md: '2rem' },
  fontWeight: 400,
  lineHeight: 1.2,
};

export const newRequestBtnSx = {
  minWidth: 160,
  borderRadius: '16px',
  textTransform: 'none',
  fontFamily: 'Questrial, sans-serif',
  fontSize: '1.125rem',
  px: 3,
  py: 1,
};

// ─── Table ────────────────────────────────────────────────────────────────────

export const tableCardStyles = {
  mt: 3,
  borderRadius: '12px',
  border: `1px solid ${colors.border}`,
  backgroundColor: colors.panel,
  overflow: 'hidden',
};

/** Compact table inside batch modal (LP list, payout preview). */
export const modalInnerTableCardSx = {
  borderRadius: '12px',
  border: `1px solid ${colors.border}`,
  backgroundColor: colors.panel,
  overflow: 'hidden',
};

export const modalInnerTableContainerSx = {
  backgroundColor: colors.panel,
  '& .MuiTable-root': { minWidth: 520 },
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textPrimary,
    py: 1.25,
    px: 2,
    verticalAlign: 'middle',
    fontFamily: 'Questrial, sans-serif',
    fontSize: '0.95rem',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: colors.tableHeaderBg,
    color: colors.textPrimary,
    fontSize: '0.95rem',
    fontWeight: 400,
    fontFamily: 'Questrial, sans-serif',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
};

export const previewSummaryGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
  gap: 1.5,
  mb: 2,
};

export const previewSummaryCardSx = {
  borderRadius: '10px',
  border: `1px solid ${colors.border}`,
  backgroundColor: '#1f2937',
  px: 2,
  py: 1.5,
};

export const previewSummaryLabelSx = {
  color: colors.textSecondary,
  fontSize: '0.8rem',
  fontFamily: 'Questrial, sans-serif',
  mb: 0.5,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

export const previewSummaryValueSx = {
  color: colors.textPrimary,
  fontSize: '1.1rem',
  fontFamily: 'Questrial, sans-serif',
  fontWeight: 400,
  lineHeight: 1.3,
};

export const tableContainerStyles = {
  backgroundColor: colors.panel,
  '& .MuiTable-root': { minWidth: 1120 },
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textPrimary,
    py: 2,
    px: 2.5,
    verticalAlign: 'top',
    fontFamily: 'Questrial, sans-serif',
    fontSize: '1rem',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: colors.tableHeaderBg,
    color: colors.textPrimary,
    fontSize: '1rem',
    fontWeight: 400,
    fontFamily: 'Questrial, sans-serif',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
  },
};

export const primaryValueStyles = {
  color: colors.textPrimary,
  fontSize: '1rem',
  fontWeight: 400,
  fontFamily: 'Questrial, sans-serif',
  lineHeight: 1.5,
};

export const monoValueStyles = { ...primaryValueStyles };

export const copyIconBtnSx = {
  color: '#ffffff',
  display: 'flex',
  flexShrink: 0,
  cursor: 'pointer',
  transition: 'color 0.2s',
  '&:hover': { color: colors.accent },
};

export const daoLinkSx = {
  ...primaryValueStyles,
  color: '#3061e8',
  textDecoration: 'underline',
  cursor: 'pointer',
};

export const statusBadgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '4px',
  py: '2px',
  pr: '8px',
  pl: '14px',
  position: 'relative',
  overflow: 'hidden',
};

export const statusBarSx = {
  position: 'absolute',
  left: '4px',
  top: '4px',
  width: '6px',
  height: '16px',
  borderRadius: '6px',
};

export const statusLabelSx = {
  fontSize: '0.875rem',
  fontFamily: 'Questrial, sans-serif',
  fontWeight: 400,
  lineHeight: '20px',
  whiteSpace: 'nowrap',
};

// ─── Pagination ───────────────────────────────────────────────────────────────

export const paginationFooterSx = {
  px: 2.5,
  py: 1.5,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 2,
  flexWrap: 'wrap',
  borderTop: `1px solid ${colors.border}`,
  backgroundColor: colors.panel,
};

export const makePaginationNavSx = (enabled) => ({
  width: 34,
  height: 34,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  border: `1px solid ${colors.border}`,
  color: enabled ? colors.textPrimary : colors.textSecondary,
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled ? 1 : 0.4,
});

export const makePaginationItemSx = (isActive, isDots) => ({
  minWidth: 34,
  height: 34,
  px: isDots ? 0.5 : 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  border: `1px solid ${isActive ? 'rgba(0, 237, 195, 0.28)' : colors.border}`,
  backgroundColor: isActive ? 'rgba(0, 237, 195, 0.14)' : 'transparent',
  color: isActive ? colors.accent : colors.textPrimary,
  fontSize: '0.9rem',
  cursor: isDots ? 'default' : 'pointer',
});

// ─── Dialog / Form ────────────────────────────────────────────────────────────

export const modalPaperStyles = {
  borderRadius: '12px',
  border: `1px solid ${colors.border}`,
  backgroundColor: '#374151',
  backgroundImage: 'none',
  minWidth: { xs: 'calc(100% - 32px)', md: 720 },
};

export const dialogTitleSx = {
  color: '#61e6bd',
  fontSize: '2.25rem',
  fontWeight: 400,
  lineHeight: 1.15,
  fontFamily: 'Questrial, sans-serif',
};

export const formGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  columnGap: 2,
  rowGap: 2,
};

export const requiredSpanSx = {
  color: '#ff3b30',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 500,
};

export const formFieldStyles = {
  '&.Mui-disabled': {
    opacity: 1,
  },
  '& .MuiInputBase-root': { borderRadius: '8px' },
  '& .MuiOutlinedInput-root': {
    color: colors.textPrimary,
    backgroundColor: '#1f2937',
    '& fieldset': { borderColor: '#374151' },
    '&:hover fieldset': { borderColor: colors.accent },
    '&.Mui-focused fieldset': { borderColor: colors.accent, borderWidth: '1px' },
    '&.Mui-error fieldset': { borderColor: '#ff3b30' },
    '&.Mui-error:hover fieldset': { borderColor: '#ff3b30' },
    '& input': { caretColor: colors.accent },
    '& textarea': { caretColor: colors.accent },
    '&.Mui-disabled': {
      backgroundColor: '#252b36',
      opacity: 1,
      '& fieldset': { borderColor: 'rgba(154, 164, 178, 0.35)' },
    },
    '&.Mui-disabled:hover fieldset': {
      borderColor: 'rgba(154, 164, 178, 0.35)',
    },
  },
  '& .MuiInputBase-input::placeholder': { color: colors.textSecondary, opacity: 1 },
  '& .MuiInputBase-input': {
    py: 1.25,
    fontSize: '1rem',
    fontFamily: 'Questrial, sans-serif',
  },
  '& .MuiInputBase-input.Mui-disabled': {
    color: '#d1d5db',
    WebkitTextFillColor: '#d1d5db',
    opacity: 1,
    cursor: 'default',
  },
  '& .MuiInputAdornment-root .MuiTypography-root': {
    color: colors.textSecondary,
    fontSize: '1rem',
    fontFamily: 'Questrial, sans-serif',
  },
  '& .MuiFormHelperText-root': {
    color: '#ff3b30',
    fontFamily: 'Questrial, sans-serif',
    fontSize: '0.625rem',
    mx: 0,
    mt: 0.5,
  },
};

/** Date inputs in revenue modals — matches formFieldStyles + dark native picker chrome. */
export const dateFormFieldStyles = {
  ...formFieldStyles,
  '& input[type="date"]': {
    colorScheme: 'dark',
    fontFamily: 'Questrial, sans-serif',
    color: colors.textPrimary,
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    cursor: 'pointer',
    opacity: 0.9,
    filter: 'invert(0.75) sepia(1) saturate(4) hue-rotate(125deg)',
  },
  '& input[type="date"]::-webkit-datetime-edit': {
    fontFamily: 'Questrial, sans-serif',
  },
  '& input[type="date"]::-webkit-datetime-edit-fields-wrapper': {
    color: colors.textPrimary,
  },
};

export const disabledFieldSx = {
  ...formFieldStyles,
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: '#252b36',
    '& fieldset': { borderColor: 'rgba(154, 164, 178, 0.35)' },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    color: '#d1d5db',
    WebkitTextFillColor: '#d1d5db',
    opacity: 1,
    fontFamily: 'Questrial, sans-serif',
    fontSize: '1rem',
    cursor: 'default',
  },
};

export const makeSelectSx = (hasError, hasValue) => ({
  borderRadius: '8px',
  backgroundColor: '#1f2937',
  fontFamily: 'Questrial, sans-serif',
  fontSize: '1rem',
  '& .MuiSelect-select': {
    py: 1.25,
    color: hasValue ? colors.textPrimary : colors.textSecondary,
    fontFamily: 'Questrial, sans-serif',
    fontSize: '1rem',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: hasError ? '#ff3b30' : '#374151' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: hasError ? '#ff3b30' : colors.accent },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? '#ff3b30' : colors.accent,
    borderWidth: '1px',
  },
  '& .MuiSvgIcon-root': { color: hasValue ? colors.textPrimary : colors.textSecondary },
});

export const selectMenuProps = {
  PaperProps: {
    sx: {
      backgroundColor: '#3e4754',
      border: '1px solid #374151',
      borderRadius: '12px',
      maxHeight: 200,
      '& .MuiList-root': { p: 0, px: 1.75 },
      '& .MuiMenuItem-root': {
        color: '#ffffff',
        fontFamily: 'Questrial, sans-serif',
        fontSize: '0.875rem',
        letterSpacing: '0.2px',
        minHeight: 40,
        borderBottom: '1px solid #374151',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
        '&.Mui-selected': { backgroundColor: 'transparent' },
        '&.Mui-selected:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
      },
    },
  },
};

export const fieldErrorTextSx = {
  color: '#ff3b30',
  fontFamily: 'Questrial, sans-serif',
  fontSize: '0.625rem',
  mt: 0.5,
};

export const dialogBtnGridSx = {
  mt: 3,
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  gap: 2,
};

export const cancelBtnSx = {
  height: 60,
  backgroundColor: '#252931',
  color: colors.accent,
  border: `1px solid ${colors.accent}`,
  borderRadius: '16px',
  textTransform: 'none',
  fontSize: '1.125rem',
  fontFamily: 'Questrial, sans-serif',
  '&:hover': { backgroundColor: '#2d3340' },
  '&.Mui-disabled': {
    backgroundColor: '#1f2329',
    color: 'rgba(0, 237, 195, 0.38)',
    borderColor: 'rgba(0, 237, 195, 0.28)',
    opacity: 1,
  },
};

export const confirmBtnSx = {
  height: 60,
  borderRadius: '16px',
  textTransform: 'none',
  fontSize: '1.125rem',
  fontFamily: 'Questrial, sans-serif',
  '&.Mui-disabled': {
    backgroundColor: 'rgba(0, 237, 195, 0.22)',
    color: 'rgba(40, 47, 56, 0.55)',
    opacity: 1,
  },
};

// ─── Form field helpers ───────────────────────────────────────────────────────

export const fieldBlockStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
};

export const fieldLabelStyles = {
  color: colors.textPrimary,
  fontSize: '1rem',
  fontFamily: 'Questrial, sans-serif',
  lineHeight: 1.5,
};

// ─── Status badge ─────────────────────────────────────────────────────────────

export const statusStyles = {
  awaiting_payment: {
    label: 'Awaiting Payment',
    color: '#ea580c',
    bgColor: 'rgba(234, 88, 12, 0.15)',
  },
  matched: {
    label: 'Matched',
    color: '#0284c7',
    bgColor: 'rgba(2, 132, 199, 0.15)',
  },
  confirmed: {
    label: 'Confirmed',
    color: '#059669',
    bgColor: 'rgba(5, 150, 105, 0.15)',
  },
  expired: {
    label: 'Expired',
    color: '#dc2626',
    bgColor: 'rgba(220, 38, 38, 0.15)',
  },
  cancelled: {
    label: 'Cancelled',
    color: '#334155',
    bgColor: '#f1f5f9',
  },
  invalid: {
    label: 'Invalid',
    color: '#dc2626',
    bgColor: 'rgba(220, 38, 38, 0.15)',
  },
  refunded: {
    label: 'Refunded',
    color: '#d97706',
    bgColor: 'rgba(217, 119, 6, 0.15)',
  },
  distributed: {
    label: 'Distributed',
    color: '#2563eb',
    bgColor: 'rgba(37, 99, 235, 0.15)',
  },
};

export const amountModeStyles = {
  fixed: {
    label: 'Fixed',
    color: '#60a5fa',
    bgColor: 'rgba(96, 165, 250, 0.2)',
  },
  flexible: {
    label: 'Flexible',
    color: '#34d399',
    bgColor: 'rgba(52, 211, 153, 0.2)',
  },
};

// ─── Toast ────────────────────────────────────────────────────────────────────

export const copyToastBoxSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  backgroundColor: '#374151',
  borderRadius: '8px',
  px: 2.5,
  py: 1.5,
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

export const successToastOuterSx = {
  width: 440,
  backgroundColor: '#374151',
  borderRadius: '8px',
  overflow: 'hidden',
  display: 'flex',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

export const successToastTitleSx = {
  color: '#f3f4f7',
  fontFamily: 'Questrial, sans-serif',
  fontSize: '1.25rem',
  fontWeight: 400,
  lineHeight: '28px',
};

export const successToastSubtitleSx = {
  color: '#a7b2c2',
  fontFamily: 'Questrial, sans-serif',
  fontSize: '1rem',
  mb: 1.5,
  pl: '32px',
};

export const successToastUrlBoxSx = {
  display: 'flex',
  borderRadius: '8px',
  border: '1px solid #374151',
  overflow: 'hidden',
  ml: '32px',
};

export const successToastUrlTextSx = {
  color: '#f3f4f7',
  fontFamily: 'Questrial, sans-serif',
  fontSize: '1rem',
};

const roleStatusBannerBaseSx = {
  mb: 2,
  px: 2.5,
  py: 1.75,
  borderRadius: '12px',
};

export const roleStatusBannerSuccessSx = {
  ...roleStatusBannerBaseSx,
  border: `1px solid rgba(26, 238, 194, 0.35)`,
  backgroundColor: 'rgba(26, 238, 194, 0.08)',
};

export const roleStatusBannerWarningSx = {
  ...roleStatusBannerBaseSx,
  border: '1px solid rgba(234, 179, 8, 0.35)',
  backgroundColor: 'rgba(234, 179, 8, 0.08)',
};

export const roleStatusBannerTextSx = {
  fontFamily: 'Questrial, sans-serif',
  fontSize: '0.95rem',
  lineHeight: 1.5,
};

export const vaultWarningBannerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 2,
  mb: 2.5,
  px: 2.5,
  py: 2,
  borderRadius: '12px',
  border: '1px solid rgba(234, 179, 8, 0.35)',
  backgroundColor: 'rgba(234, 179, 8, 0.08)',
};

export const vaultWarningContentSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flex: 1,
  minWidth: 0,
};

export const vaultWarningTextSx = {
  fontFamily: 'Questrial, sans-serif',
  fontSize: '1rem',
  lineHeight: 1.5,
  color: '#eab308',
};

export const vaultWarningActionBtnSx = {
  color: '#eab308',
  borderColor: '#eab308',
  borderRadius: '10px',
  textTransform: 'none',
  fontFamily: 'Questrial, sans-serif',
  fontSize: '0.9rem',
  whiteSpace: 'nowrap',
  '&:hover': { borderColor: '#eab308', backgroundColor: 'rgba(234, 179, 8, 0.1)' },
};

export const successToastCopyBtnSx = {
  backgroundColor: '#1f2937',
  borderLeft: '1px solid #374151',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: 1.5,
  cursor: 'pointer',
  flexShrink: 0,
  '&:hover': { backgroundColor: '#252f3e' },
};
